import { useEffect, useRef } from 'react';
import { TableIds } from 'src/api/types/misc';
import useLocalStorage from 'src/hooks/useLocalStorage';

interface ComputeEnsuredColumnsArgs {
  /** The user's current (possibly saved) column selection. */
  selected: string[];
  /** Keys already injected once for this table — never touched again. */
  ensured: string[];
  /** Default column keys that must appear at least once for every user. */
  ensure: string[];
}

interface EnsuredColumnsResult {
  nextSelected: string[];
  nextEnsured: string[];
}

/**
 * One-shot merge of newly-defaulted column keys into a saved column set. A key
 * recorded in `ensured` is never injected again, so a later manual uncheck sticks.
 * Returns null when every ensure key was already handled.
 */
export const computeEnsuredColumns = ({
  selected,
  ensured,
  ensure,
}: ComputeEnsuredColumnsArgs): EnsuredColumnsResult | null => {
  const missing = ensure.filter((key) => !ensured.includes(key));
  if (!missing.length) return null;

  const toAdd = missing.filter((key) => !selected.includes(key));

  return {
    nextSelected: toAdd.length ? [...selected, ...toAdd] : selected,
    nextEnsured: [...ensured, ...missing],
  };
};

/**
 * Saved column sets come back from localStorage verbatim, so a column newly added to a
 * table's defaults never appears for users with a pre-existing set (CRPD-7209 feedback:
 * "Linked RMP Controls" / Annual Cost must be open after deployment). Injects each
 * ensure key exactly once per table, recorded under `${tableId}-columns-ensured`.
 * Rendered position is unaffected — column order comes from the column-definition
 * order, not the selection array.
 */
export const useEnsureDefaultColumns = (
  tableId: TableIds,
  selected: string[],
  setSelected: (value: string[]) => void,
  ensure: string[],
): void => {
  const [ensured, setEnsured] = useLocalStorage<string[]>(`${tableId}-columns-ensured`, []);
  const ran = useRef(false);

  useEffect(() => {
    if (ran.current) return;
    ran.current = true;

    const next = computeEnsuredColumns({ selected, ensured, ensure });
    if (!next) return;

    if (next.nextSelected !== selected) setSelected(next.nextSelected);
    setEnsured(next.nextEnsured);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
};
